import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { VRM, VRMLoaderPlugin, VRMHumanBoneName } from '@pixiv/three-vrm';
import { VRMAnimationLoaderPlugin, VRMAnimation, createVRMAnimationClip } from '@pixiv/three-vrm-animation';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { VRMCacheManager } from '../services/vrmCacheManager';
import { globalVRMCache, VRM_ANIMATION_PATHS, VRM_MODEL_PATH, ANIMATION_CONFIG } from '../constants/vrmConstants';

const vrmCache = new VRMCacheManager();

interface VRMAvatarProps {
  audioUrl?: string;
  isPlaying?: boolean;
  isPaused?: boolean;
  onLoadComplete?: () => void;
  onAudioEnd?: () => void;
  playGreeting?: boolean;
  greetingTrigger?: number;
  externalAnalyser?: AnalyserNode | null;
  externalAnalyserData?: Uint8Array | null;
}


const VRMAvatarModel: React.FC<VRMAvatarProps> = ({ audioUrl, isPlaying, isPaused, onLoadComplete, onAudioEnd, playGreeting, greetingTrigger, externalAnalyser, externalAnalyserData }) => {

  // Helper function to build proxy URL from a relative path
  const toProxyUrl = (path: string): string => `/api/proxy/vrm/${path}`;

  // Helper function to load files with IndexedDB caching
  const loadFileWithCache = async (url: string): Promise<ArrayBuffer> => {
    const loadStartTime = Date.now();

    // Try to get from IndexedDB cache first
    const cachedData = await vrmCache.getFromCache(url);
    if (cachedData) {
      const loadTime = (Date.now() - loadStartTime) / 1000;
      return cachedData;
    }

    // Not in cache, fetch from network

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', url, true);
      xhr.responseType = 'arraybuffer';

      xhr.onload = async () => {
        if (xhr.status === 200) {
          const loadTime = (Date.now() - loadStartTime) / 1000;
          const data = xhr.response as ArrayBuffer;
          const sizeMB = (data.byteLength / 1024 / 1024).toFixed(2);

          // Cache for future use (don't wait for this to complete)
          vrmCache.saveToCache(url, data).catch(error =>
            console.warn('Failed to cache file:', error)
          );

          resolve(data);
        } else {
          reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`));
        }
      };

      xhr.onerror = () => reject(new Error('Network error'));
      xhr.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentComplete = (event.loaded / event.total * 100).toFixed(1);
          const loadedMB = (event.loaded / 1024 / 1024).toFixed(2);
          const totalMB = (event.total / 1024 / 1024).toFixed(2);
        }
      };

      xhr.send();
    });
  };

  const vrmRef = useRef<VRM | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const dataArrayRef = useRef<Uint8Array | null>(null);
  const mixerRef = useRef<THREE.AnimationMixer | null>(null);
  const idleActionRef = useRef<THREE.AnimationAction | null>(null);
  const greetingActionRef = useRef<THREE.AnimationAction | null>(null);
  const speakingActionRef = useRef<THREE.AnimationAction | null>(null);
  const isGreetingPlayingRef = useRef<boolean>(false);
  const { scene } = useThree();
  const [isLoaded, setIsLoaded] = useState(false);

  const setupAudioAnalysis = useCallback((audio: HTMLAudioElement) => {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const source = audioContext.createMediaElementSource(audio);
    const analyser = audioContext.createAnalyser();

    // Optimize for better voice detection
    analyser.fftSize = 512;
    analyser.smoothingTimeConstant = 0.3; // Faster response
    analyser.minDecibels = -90;
    analyser.maxDecibels = -10;

    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    source.connect(analyser);
    analyser.connect(audioContext.destination);

    analyserRef.current = analyser;
    dataArrayRef.current = dataArray;
  }, []);

  const calculateVisemeData = useCallback(() => {
    // Use external analyser if available (from Web Audio API), otherwise use internal analyser
    const analyser = externalAnalyser || analyserRef.current;
    const dataArray = externalAnalyserData || dataArrayRef.current;

    if (!analyser || !dataArray) {
      return { viseme: 'sil', intensity: 0, frequencies: [] };
    }

    analyser.getByteFrequencyData(dataArray);
    const freqData = dataArray;

    // Define frequency ranges for different visemes (phonemes)
    // Based on formant frequencies for English phonemes
    const frequencyRanges = {
      // Low frequencies (fundamental + low formants)
      lowFreq: freqData.slice(1, 8),     // ~20-320Hz - Vowels, nasals
      midFreq: freqData.slice(8, 25),    // ~320-1000Hz - Main formant area
      highFreq: freqData.slice(25, 50),  // ~1000-2000Hz - High formants
      fricatives: freqData.slice(50, 100) // ~2000-4000Hz - Fricatives (s, sh, f, th)
    };

    // Calculate energy in each frequency band
    const lowEnergy = frequencyRanges.lowFreq.reduce((sum, val) => sum + val, 0) / frequencyRanges.lowFreq.length;
    const midEnergy = frequencyRanges.midFreq.reduce((sum, val) => sum + val, 0) / frequencyRanges.midFreq.length;
    const highEnergy = frequencyRanges.highFreq.reduce((sum, val) => sum + val, 0) / frequencyRanges.highFreq.length;
    const fricativeEnergy = frequencyRanges.fricatives.reduce((sum, val) => sum + val, 0) / frequencyRanges.fricatives.length;

    const totalEnergy = (lowEnergy + midEnergy + highEnergy + fricativeEnergy) / 4;
    const intensity = Math.min(totalEnergy / 60, 1.0);

    // Silence threshold
    if (intensity < ANIMATION_CONFIG.SPEAKING_VOLUME_THRESHOLD) {
      return { viseme: 'sil', intensity: 0, frequencies: [lowEnergy, midEnergy, highEnergy, fricativeEnergy] };
    }

    // Viseme detection based on frequency analysis
    let detectedViseme = 'aa'; // Default to open vowel

    // High fricative energy = fricatives and affricates
    if (fricativeEnergy > midEnergy * 1.5 && fricativeEnergy > 25) {
      if (highEnergy > midEnergy) {
        detectedViseme = 'S'; // 's', 'z', 'sh', 'zh'
      } else {
        detectedViseme = 'F'; // 'f', 'v', 'th'
      }
    }
    // High mid-frequency with moderate high frequency = vowels
    else if (midEnergy > lowEnergy && midEnergy > 20) {
      const formantRatio = highEnergy / midEnergy;

      if (formantRatio > 0.8) {
        detectedViseme = 'E'; // 'e', 'i' - high front vowels
      } else if (formantRatio > 0.5) {
        detectedViseme = 'aa'; // 'a' - open vowels
      } else {
        // Distinguish between 'O' and 'U' based on energy distribution
        const lowMidRatio = lowEnergy / midEnergy;
        if (lowMidRatio > 0.7 && fricativeEnergy < 15) {
          detectedViseme = 'U'; // 'u', 'oo' - tight rounded vowels
        } else {
          detectedViseme = 'O'; // 'o', 'aw' - open rounded vowels
        }
      }
    }
    // High low-frequency energy = nasals and consonants
    else if (lowEnergy > midEnergy && lowEnergy > 15) {
      if (midEnergy < 10) {
        detectedViseme = 'M'; // 'm', 'b', 'p' - bilabials
      } else {
        detectedViseme = 'L'; // 'l', 'n', 't', 'd' - alveolars
      }
    }


    // Debug log for viseme detection
    if (intensity > ANIMATION_CONFIG.SPEAKING_VOLUME_THRESHOLD) {
    }

    return {
      viseme: detectedViseme,
      intensity,
      frequencies: [lowEnergy, midEnergy, highEnergy, fricativeEnergy]
    };
  }, [externalAnalyser, externalAnalyserData]);

  // Function to immediately reset all facial expressions to neutral
  const resetFacialExpressions = useCallback(() => {
    if (!vrmRef.current) return;

    const expressionManager = vrmRef.current.expressionManager;
    if (!expressionManager) return;

    // Helper function to try setting a blend shape with multiple name variations
    const trySetBlendShape = (nameVariations: string[], value: number) => {
      const clampedValue = Math.min(Math.max(value, 0), 1);
      for (const name of nameVariations) {
        try {
          expressionManager.setValue(name, clampedValue);
        } catch (error) {
          // Continue to next variation
        }
      }
    };

    // ARKit blend shape name variations
    const arkitShapes = {
      // JAW EXPRESSIONS
      jawOpen: ['jawOpen', 'JawOpen', 'jaw_open'],
      jawForward: ['jawForward', 'JawForward', 'jaw_forward'],
      jawLeft: ['jawLeft', 'JawLeft', 'jaw_left'],
      jawRight: ['jawRight', 'JawRight', 'jaw_right'],

      // MOUTH EXPRESSIONS
      mouthClose: ['mouthClose', 'MouthClose', 'mouth_close'],
      mouthFunnel: ['mouthFunnel', 'MouthFunnel', 'mouth_funnel'],
      mouthPucker: ['mouthPucker', 'MouthPucker', 'mouth_pucker'],
      mouthSmileLeft: ['mouthSmileLeft', 'MouthSmileLeft', 'mouth_smile_left'],
      mouthSmileRight: ['mouthSmileRight', 'MouthSmileRight', 'mouth_smile_right'],
      mouthFrownLeft: ['mouthFrownLeft', 'MouthFrownLeft', 'mouth_frown_left'],
      mouthFrownRight: ['mouthFrownRight', 'MouthFrownRight', 'mouth_frown_right'],
      mouthDimpleLeft: ['mouthDimpleLeft', 'MouthDimpleLeft', 'mouth_dimple_left'],
      mouthDimpleRight: ['mouthDimpleRight', 'MouthDimpleRight', 'mouth_dimple_right'],
      mouthStretchLeft: ['mouthStretchLeft', 'MouthStretchLeft', 'mouth_stretch_left'],
      mouthStretchRight: ['mouthStretchRight', 'MouthStretchRight', 'mouth_stretch_right'],
      mouthRollLower: ['mouthRollLower', 'MouthRollLower', 'mouth_roll_lower'],
      mouthRollUpper: ['mouthRollUpper', 'MouthRollUpper', 'mouth_roll_upper'],
      mouthShrugLower: ['mouthShrugLower', 'MouthShrugLower', 'mouth_shrug_lower'],
      mouthShrugUpper: ['mouthShrugUpper', 'MouthShrugUpper', 'mouth_shrug_upper'],
      mouthPressLeft: ['mouthPressLeft', 'MouthPressLeft', 'mouth_press_left'],
      mouthPressRight: ['mouthPressRight', 'MouthPressRight', 'mouth_press_right'],
      mouthLowerDownLeft: ['mouthLowerDownLeft', 'MouthLowerDownLeft', 'mouth_lower_down_left'],
      mouthLowerDownRight: ['mouthLowerDownRight', 'MouthLowerDownRight', 'mouth_lower_down_right'],
      mouthUpperUpLeft: ['mouthUpperUpLeft', 'MouthUpperUpLeft', 'mouth_upper_up_left'],
      mouthUpperUpRight: ['mouthUpperUpRight', 'MouthUpperUpRight', 'mouth_upper_up_right'],

      // EYE EXPRESSIONS
      eyeBlinkLeft: ['eyeBlinkLeft', 'EyeBlinkLeft', 'eye_blink_left'],
      eyeBlinkRight: ['eyeBlinkRight', 'EyeBlinkRight', 'eye_blink_right'],
      eyeWideLeft: ['eyeWideLeft', 'EyeWideLeft', 'eye_wide_left'],
      eyeWideRight: ['eyeWideRight', 'EyeWideRight', 'eye_wide_right'],
      eyeSquintLeft: ['eyeSquintLeft', 'EyeSquintLeft', 'eye_squint_left'],
      eyeSquintRight: ['eyeSquintRight', 'EyeSquintRight', 'eye_squint_right'],

      // EYEBROW EXPRESSIONS
      browInnerUp: ['browInnerUp', 'BrowInnerUp', 'brow_inner_up'],
      browOuterUpLeft: ['browOuterUpLeft', 'BrowOuterUpLeft', 'brow_outer_up_left'],
      browOuterUpRight: ['browOuterUpRight', 'BrowOuterUpRight', 'brow_outer_up_right'],

      // CHEEK EXPRESSIONS
      cheekSquintLeft: ['cheekSquintLeft', 'CheekSquintLeft', 'cheek_squint_left'],
      cheekSquintRight: ['cheekSquintRight', 'CheekSquintRight', 'cheek_squint_right'],
      cheekPuff: ['cheekPuff', 'CheekPuff', 'cheek_puff'],

      // NOSE EXPRESSIONS
      noseSneerLeft: ['noseSneerLeft', 'NoseSneerLeft', 'nose_sneer_left'],
      noseSneerRight: ['noseSneerRight', 'NoseSneerRight', 'nose_sneer_right'],

      // MISC
      tongueOut: ['tongueOut', 'TongueOut', 'tongue_out'],
    };

    // Reset ALL expressions to neutral (0)
    Object.values(arkitShapes).forEach(nameVariations => {
      trySetBlendShape(nameVariations, 0);
    });

  }, []);

  const updateBlendShapes = useCallback((visemeData: {viseme: string, intensity: number}, headRotation?: {x: number, y: number}, neckRotation?: {x: number, y: number}) => {
    if (!vrmRef.current) return;

    const expressionManager = vrmRef.current.expressionManager;
    if (!expressionManager) return;

    // Using viseme-based intensity instead of raw audio level

    // Complete ARKit 52 blend shape names with variations
    const arkitShapes = {
      // JAW MOVEMENT
      jawOpen: ['jawOpen', 'JawOpen', 'jaw_open'],
      jawForward: ['jawForward', 'JawForward', 'jaw_forward'], // Forward jaw movement - KEY FOR TEETH!
      jawLeft: ['jawLeft', 'JawLeft', 'jaw_left'], // Lateral jaw movement
      jawRight: ['jawRight', 'JawRight', 'jaw_right'], // Lateral jaw movement

      // MOUTH EXPRESSIONS
      mouthClose: ['mouthClose', 'MouthClose', 'mouth_close'], // Independent lip closure
      mouthFunnel: ['mouthFunnel', 'MouthFunnel', 'mouth_funnel'], // OO sound
      mouthPucker: ['mouthPucker', 'MouthPucker', 'mouth_pucker'], // U sound
      mouthLeft: ['mouthLeft', 'MouthLeft', 'mouth_left'],
      mouthRight: ['mouthRight', 'MouthRight', 'mouth_right'],

      // Mouth smiling and stretching
      mouthSmileLeft: ['mouthSmileLeft', 'MouthSmileLeft', 'mouth_smile_left'], // E, I sounds
      mouthSmileRight: ['mouthSmileRight', 'MouthSmileRight', 'mouth_smile_right'], // E, I sounds
      mouthFrownLeft: ['mouthFrownLeft', 'MouthFrownLeft', 'mouth_frown_left'],
      mouthFrownRight: ['mouthFrownRight', 'MouthFrownRight', 'mouth_frown_right'],
      mouthDimpleLeft: ['mouthDimpleLeft', 'MouthDimpleLeft', 'mouth_dimple_left'],
      mouthDimpleRight: ['mouthDimpleRight', 'MouthDimpleRight', 'mouth_dimple_right'],
      mouthStretchLeft: ['mouthStretchLeft', 'MouthStretchLeft', 'mouth_stretch_left'], // Wide A
      mouthStretchRight: ['mouthStretchRight', 'MouthStretchRight', 'mouth_stretch_right'], // Wide A

      // Lip rolling and pressing
      mouthRollLower: ['mouthRollLower', 'MouthRollLower', 'mouth_roll_lower'], // M, P, B
      mouthRollUpper: ['mouthRollUpper', 'MouthRollUpper', 'mouth_roll_upper'], // M, P, B
      mouthShrugLower: ['mouthShrugLower', 'MouthShrugLower', 'mouth_shrug_lower'],
      mouthShrugUpper: ['mouthShrugUpper', 'MouthShrugUpper', 'mouth_shrug_upper'],
      mouthPressLeft: ['mouthPressLeft', 'MouthPressLeft', 'mouth_press_left'],
      mouthPressRight: ['mouthPressRight', 'MouthPressRight', 'mouth_press_right'],

      // Specific lip movements
      mouthLowerDownLeft: ['mouthLowerDownLeft', 'MouthLowerDownLeft', 'mouth_lower_down_left'],
      mouthLowerDownRight: ['mouthLowerDownRight', 'MouthLowerDownRight', 'mouth_lower_down_right'],
      mouthUpperUpLeft: ['mouthUpperUpLeft', 'MouthUpperUpLeft', 'mouth_upper_up_left'],
      mouthUpperUpRight: ['mouthUpperUpRight', 'MouthUpperUpRight', 'mouth_upper_up_right'],

      // EYE EXPRESSIONS
      eyeBlinkLeft: ['eyeBlinkLeft', 'EyeBlinkLeft', 'eye_blink_left'],
      eyeBlinkRight: ['eyeBlinkRight', 'EyeBlinkRight', 'eye_blink_right'],
      eyeLookDownLeft: ['eyeLookDownLeft', 'EyeLookDownLeft', 'eye_look_down_left'],
      eyeLookDownRight: ['eyeLookDownRight', 'EyeLookDownRight', 'eye_look_down_right'],
      eyeLookInLeft: ['eyeLookInLeft', 'EyeLookInLeft', 'eye_look_in_left'],
      eyeLookInRight: ['eyeLookInRight', 'EyeLookInRight', 'eye_look_in_right'],
      eyeLookOutLeft: ['eyeLookOutLeft', 'EyeLookOutLeft', 'eye_look_out_left'],
      eyeLookOutRight: ['eyeLookOutRight', 'EyeLookOutRight', 'eye_look_out_right'],
      eyeLookUpLeft: ['eyeLookUpLeft', 'EyeLookUpLeft', 'eye_look_up_left'],
      eyeLookUpRight: ['eyeLookUpRight', 'EyeLookUpRight', 'eye_look_up_right'],
      eyeSquintLeft: ['eyeSquintLeft', 'EyeSquintLeft', 'eye_squint_left'],
      eyeSquintRight: ['eyeSquintRight', 'EyeSquintRight', 'eye_squint_right'],
      eyeWideLeft: ['eyeWideLeft', 'EyeWideLeft', 'eye_wide_left'],
      eyeWideRight: ['eyeWideRight', 'EyeWideRight', 'eye_wide_right'],

      // EYEBROW EXPRESSIONS
      browDownLeft: ['browDownLeft', 'BrowDownLeft', 'brow_down_left'],
      browDownRight: ['browDownRight', 'BrowDownRight', 'brow_down_right'],
      browInnerUp: ['browInnerUp', 'BrowInnerUp', 'brow_inner_up'],
      browOuterUpLeft: ['browOuterUpLeft', 'BrowOuterUpLeft', 'brow_outer_up_left'],
      browOuterUpRight: ['browOuterUpRight', 'BrowOuterUpRight', 'brow_outer_up_right'],

      // CHEEK EXPRESSIONS
      cheekPuff: ['cheekPuff', 'CheekPuff', 'cheek_puff'],
      cheekSquintLeft: ['cheekSquintLeft', 'CheekSquintLeft', 'cheek_squint_left'],
      cheekSquintRight: ['cheekSquintRight', 'CheekSquintRight', 'cheek_squint_right'],

      // NOSE EXPRESSIONS
      noseSneerLeft: ['noseSneerLeft', 'NoseSneerLeft', 'nose_sneer_left'],
      noseSneerRight: ['noseSneerRight', 'NoseSneerRight', 'nose_sneer_right'],

      // TONGUE EXPRESSIONS
      tongueOut: ['tongueOut', 'TongueOut', 'tongue_out'],
    };


    // Helper function to try setting a blend shape with multiple name variations
    // Apply matching expressions
    const trySetBlendShape = (nameVariations: string[], value: number) => {
      const clampedValue = Math.min(Math.max(value, 0), 1);

      for (const name of nameVariations) {
        try {
          expressionManager.setValue(name, clampedValue);
        } catch (error) {
          // Continue to next variation - don't log errors for non-existent expressions
        }
      }
    };

    // Enhanced time-based animations for natural facial expressions
    const time = Date.now() / 1000;
    const slowTime = time * 0.3; // Very slow for natural movements
    const fastTime = time * 2.0; // Faster for speech-related changes

    // Extract viseme and intensity from the passed data
    const { viseme, intensity } = visemeData;

    // VISEME-BASED LIPSYNC SYSTEM - PROPER PHONEME TO MOUTH SHAPE MAPPING
    if (intensity > ANIMATION_CONFIG.SPEAKING_VOLUME_THRESHOLD) { // Only start animating above noise threshold

      // VISEME-BASED MOUTH SHAPES
      // Map detected viseme to appropriate mouth shapes

      switch (viseme) {
        case 'sil': // Silence - neutral position
          // Mouth stays in neutral position (reset below)
          break;

        case 'aa': // Open vowels: 'a', 'ah', 'aw'
          trySetBlendShape(arkitShapes.jawOpen, 1.0);
          trySetBlendShape(arkitShapes.mouthFunnel, 0.3);
          break;

        case 'E': // Front vowels: 'e', 'i', 'ay', 'eh'
          trySetBlendShape(arkitShapes.jawOpen, 0.4);
          trySetBlendShape(arkitShapes.mouthSmileLeft, 0.8);
          trySetBlendShape(arkitShapes.mouthSmileRight, 0.8);
          trySetBlendShape(arkitShapes.mouthStretchLeft, 0.5);
          trySetBlendShape(arkitShapes.mouthStretchRight, 0.5);
          break;

        case 'O': // Back rounded vowels: 'o', 'aw', 'ow'
          trySetBlendShape(arkitShapes.jawOpen, 0.6);
          trySetBlendShape(arkitShapes.mouthPucker, 0.7);
          trySetBlendShape(arkitShapes.mouthFunnel, 0.5);
          break;

        case 'U': // Tight rounded vowels: 'u', 'oo', 'you'
          trySetBlendShape(arkitShapes.jawOpen, 0.3);
          trySetBlendShape(arkitShapes.mouthPucker, 1.0);
          trySetBlendShape(arkitShapes.mouthFunnel, 0.9);
          break;

        case 'M': // Bilabials: 'm', 'b', 'p'
          // Lips closed/pressed together
          trySetBlendShape(arkitShapes.jawOpen, 0.1);
          trySetBlendShape(arkitShapes.mouthPressLeft, 0.8);
          trySetBlendShape(arkitShapes.mouthPressRight, 0.8);
          trySetBlendShape(arkitShapes.mouthRollLower, 0.6);
          trySetBlendShape(arkitShapes.mouthRollUpper, 0.6);
          break;

        case 'L': // Alveolars: 'l', 'n', 't', 'd', 'r'
          trySetBlendShape(arkitShapes.jawOpen, 0.5);
          trySetBlendShape(arkitShapes.jawForward, 0.3);
          // Slight tongue involvement
          trySetBlendShape(arkitShapes.tongueOut, 0.2);
          break;

        case 'S': // Fricatives: 's', 'z', 'sh', 'zh'
          trySetBlendShape(arkitShapes.jawOpen, 0.3);
          trySetBlendShape(arkitShapes.mouthFunnel, 0.4);
          trySetBlendShape(arkitShapes.mouthPucker, 0.3);
          // Slight smile for sibilants
          trySetBlendShape(arkitShapes.mouthSmileLeft, 0.3);
          trySetBlendShape(arkitShapes.mouthSmileRight, 0.3);
          break;

        case 'F': // Labiodentals: 'f', 'v', 'th'
          trySetBlendShape(arkitShapes.jawOpen, 0.2);
          trySetBlendShape(arkitShapes.mouthLowerDownLeft, 0.6);
          trySetBlendShape(arkitShapes.mouthLowerDownRight, 0.6);
          break;

        default:
          // Fallback to neutral open position
          trySetBlendShape(arkitShapes.jawOpen, 0.5);
      }

      // JAW MOVEMENT BASED ON VISEME
      // Natural jaw movement variation
      const jawVariation = 0.9 + Math.sin(fastTime * 2) * 0.1; // Subtle 0.8-1.0 variation
      const currentJawValue = jawVariation;

      // Only override jaw if not already set by viseme
      if (viseme === 'sil' || viseme === 'aa') {
        trySetBlendShape(arkitShapes.jawOpen, currentJawValue);
      }

      // SUPPORTING FACIAL EXPRESSIONS
      // Eye expressions that enhance speech (less dominant than visemes)
      const eyeIntensityBase = 0.4; // Fixed impact
      const eyeVariation = Math.sin(fastTime * 1.2) * 0.2 + 0.8; // Subtle variation
      trySetBlendShape(arkitShapes.eyeWideLeft, eyeIntensityBase * eyeVariation);
      trySetBlendShape(arkitShapes.eyeWideRight, eyeIntensityBase * eyeVariation);

      // Eyebrow expressions for emphasis (fixed values)
      const browIntensity = 0.6; // Fixed range
      trySetBlendShape(arkitShapes.browInnerUp, browIntensity * 0.7);
      trySetBlendShape(arkitShapes.browOuterUpLeft, browIntensity * 0.4);
      trySetBlendShape(arkitShapes.browOuterUpRight, browIntensity * 0.4);

      // Cheek expressions for certain visemes
      if (viseme === 'E' || viseme === 'S') {
        // Slight cheek lift for smiling visemes
        trySetBlendShape(arkitShapes.cheekSquintLeft, 0.3);
        trySetBlendShape(arkitShapes.cheekSquintRight, 0.3);
      }

      // Nose expressions for nasal sounds
      if (viseme === 'M' || viseme === 'L') {
        const noseIntensity = 0.2;
        trySetBlendShape(arkitShapes.noseSneerLeft, noseIntensity);
        trySetBlendShape(arkitShapes.noseSneerRight, noseIntensity);
      }

    } else {
      // IDLE FACIAL EXPRESSIONS (when not speaking)

      // Reset all speech-related expressions to neutral
      trySetBlendShape(arkitShapes.jawOpen, 0);
      trySetBlendShape(arkitShapes.jawForward, 0);
      trySetBlendShape(arkitShapes.jawLeft, 0);
      trySetBlendShape(arkitShapes.jawRight, 0);

      // Reset mouth expressions
      trySetBlendShape(arkitShapes.mouthSmileLeft, 0);
      trySetBlendShape(arkitShapes.mouthSmileRight, 0);
      trySetBlendShape(arkitShapes.mouthFrownLeft, 0);
      trySetBlendShape(arkitShapes.mouthFrownRight, 0);
      trySetBlendShape(arkitShapes.mouthPucker, 0);
      trySetBlendShape(arkitShapes.mouthFunnel, 0);
      trySetBlendShape(arkitShapes.mouthPressLeft, 0);
      trySetBlendShape(arkitShapes.mouthPressRight, 0);
      trySetBlendShape(arkitShapes.mouthStretchLeft, 0);
      trySetBlendShape(arkitShapes.mouthStretchRight, 0);
      trySetBlendShape(arkitShapes.mouthUpperUpLeft, 0);
      trySetBlendShape(arkitShapes.mouthUpperUpRight, 0);
      trySetBlendShape(arkitShapes.mouthLowerDownLeft, 0);
      trySetBlendShape(arkitShapes.mouthLowerDownRight, 0);
      trySetBlendShape(arkitShapes.mouthDimpleLeft, 0);
      trySetBlendShape(arkitShapes.mouthDimpleRight, 0);

      // Reset eye speech expressions (but keep natural idle ones)
      trySetBlendShape(arkitShapes.eyeWideLeft, 0);
      trySetBlendShape(arkitShapes.eyeWideRight, 0);
      trySetBlendShape(arkitShapes.eyeSquintLeft, 0);
      trySetBlendShape(arkitShapes.eyeSquintRight, 0);

      // Reset cheek and nose speech expressions
      trySetBlendShape(arkitShapes.cheekPuff, 0);
      trySetBlendShape(arkitShapes.tongueOut, 0);
      trySetBlendShape(arkitShapes.cheekSquintLeft, 0);
      trySetBlendShape(arkitShapes.cheekSquintRight, 0);

      // Reset eyebrow speech expressions
      trySetBlendShape(arkitShapes.browInnerUp, 0);
      trySetBlendShape(arkitShapes.browOuterUpLeft, 0);
      trySetBlendShape(arkitShapes.browOuterUpRight, 0);

      // Reset nose speech expressions
      trySetBlendShape(arkitShapes.noseSneerLeft, 0);
      trySetBlendShape(arkitShapes.noseSneerRight, 0);

      // Reset mouth rolling expressions
      trySetBlendShape(arkitShapes.mouthRollLower, 0);
      trySetBlendShape(arkitShapes.mouthRollUpper, 0);
      trySetBlendShape(arkitShapes.mouthShrugLower, 0);
      trySetBlendShape(arkitShapes.mouthShrugUpper, 0);

      // Continue natural idle expressions (blinking, subtle movements) below
    }

    // CONTINUOUS FACIAL EXPRESSIONS (always active)

    // Natural blinking animation
    const blinkCycle = Math.sin(time * 3.5 + Math.sin(time * 0.7) * 2) * 0.5 + 0.5; // Irregular blinking
    if (blinkCycle > 0.85) { // Blink when cycle is high
      const blinkIntensity = Math.min((blinkCycle - 0.85) * 6.67, 1.0); // Scale 0.85-1.0 to 0-1
      trySetBlendShape(arkitShapes.eyeBlinkLeft, blinkIntensity);
      trySetBlendShape(arkitShapes.eyeBlinkRight, blinkIntensity);
    } else {
      trySetBlendShape(arkitShapes.eyeBlinkLeft, 0);
      trySetBlendShape(arkitShapes.eyeBlinkRight, 0);
    }

    // Natural eyebrow and nose variations for life-like appearance (only when not speaking)
    if (intensity <= ANIMATION_CONFIG.SPEAKING_VOLUME_THRESHOLD) {
      const browVariation = Math.sin(slowTime) * 0.1 + 0.1; // Subtle 0 to 0.2
      trySetBlendShape(arkitShapes.browOuterUpLeft, browVariation);
      trySetBlendShape(arkitShapes.browOuterUpRight, browVariation);

      // Very subtle nose expressions
      const noseVariation = Math.sin(slowTime * 1.3) * 0.05 + 0.05; // Very subtle 0 to 0.1
      trySetBlendShape(arkitShapes.noseSneerLeft, noseVariation);
      trySetBlendShape(arkitShapes.noseSneerRight, noseVariation);
    }

    // CAMERA-LOOKING EYE MOVEMENT
    if (headRotation && neckRotation) {
      // Total head rotation (head + neck)
      const totalYRotation = headRotation.y + neckRotation.y;
      const totalXRotation = headRotation.x;

      // Eye movement to compensate for head rotation and look at camera
      const eyeLookIntensity = 0.6; // Strength of camera-looking behavior

      // Horizontal eye movement (left/right) - compensate for head Y rotation
      if (totalYRotation > 0) {
        // Head turned left, eyes look right to maintain camera contact
        const eyeRightIntensity = Math.min(Math.abs(totalYRotation) * eyeLookIntensity, 0.8);
        trySetBlendShape(arkitShapes.eyeLookInLeft, eyeRightIntensity);
        trySetBlendShape(arkitShapes.eyeLookOutRight, eyeRightIntensity);
        // Reset opposite direction
        trySetBlendShape(arkitShapes.eyeLookOutLeft, 0);
        trySetBlendShape(arkitShapes.eyeLookInRight, 0);
      } else if (totalYRotation < 0) {
        // Head turned right, eyes look left to maintain camera contact
        const eyeLeftIntensity = Math.min(Math.abs(totalYRotation) * eyeLookIntensity, 0.8);
        trySetBlendShape(arkitShapes.eyeLookOutLeft, eyeLeftIntensity);
        trySetBlendShape(arkitShapes.eyeLookInRight, eyeLeftIntensity);
        // Reset opposite direction
        trySetBlendShape(arkitShapes.eyeLookInLeft, 0);
        trySetBlendShape(arkitShapes.eyeLookOutRight, 0);
      } else {
        // Head centered, eyes look forward
        trySetBlendShape(arkitShapes.eyeLookInLeft, 0);
        trySetBlendShape(arkitShapes.eyeLookOutLeft, 0);
        trySetBlendShape(arkitShapes.eyeLookInRight, 0);
        trySetBlendShape(arkitShapes.eyeLookOutRight, 0);
      }

      // Vertical eye movement (up/down) - compensate for head X rotation
      if (totalXRotation > 0) {
        // Head tilted down, eyes look up to maintain camera contact
        const eyeUpIntensity = Math.min(Math.abs(totalXRotation) * eyeLookIntensity * 1.5, 0.6);
        trySetBlendShape(arkitShapes.eyeLookUpLeft, eyeUpIntensity);
        trySetBlendShape(arkitShapes.eyeLookUpRight, eyeUpIntensity);
        // Reset opposite direction
        trySetBlendShape(arkitShapes.eyeLookDownLeft, 0);
        trySetBlendShape(arkitShapes.eyeLookDownRight, 0);
      } else if (totalXRotation < 0) {
        // Head tilted up, eyes look down to maintain camera contact
        const eyeDownIntensity = Math.min(Math.abs(totalXRotation) * eyeLookIntensity * 1.5, 0.6);
        trySetBlendShape(arkitShapes.eyeLookDownLeft, eyeDownIntensity);
        trySetBlendShape(arkitShapes.eyeLookDownRight, eyeDownIntensity);
        // Reset opposite direction
        trySetBlendShape(arkitShapes.eyeLookUpLeft, 0);
        trySetBlendShape(arkitShapes.eyeLookUpRight, 0);
      } else {
        // Head level, eyes look straight
        trySetBlendShape(arkitShapes.eyeLookUpLeft, 0);
        trySetBlendShape(arkitShapes.eyeLookUpRight, 0);
        trySetBlendShape(arkitShapes.eyeLookDownLeft, 0);
        trySetBlendShape(arkitShapes.eyeLookDownRight, 0);
      }
    }

  }, []);


  useEffect(() => {
    // Clear any existing VRM from the scene first
    scene.traverse((child) => {
      if (child.userData.vrm) {
        scene.remove(child);
      }
    });

    // Check if VRM is already cached in memory
    if (globalVRMCache.main && globalVRMCache.mainLoaded) {
      const vrm = globalVRMCache.main;
      setupVRM(vrm);
    } else {
      const loader = new GLTFLoader();
      loader.register((parser) => new VRMLoaderPlugin(parser));

      const vrmUrl = toProxyUrl(VRM_MODEL_PATH);

      // Debug the database before attempting to cache
      vrmCache.debugDatabase();

      // Load with IndexedDB caching
      loadFileWithCache(vrmUrl)
        .then((arrayBuffer) => {
          // Convert ArrayBuffer to Blob for GLTFLoader
          const blob = new Blob([arrayBuffer]);
          const url = URL.createObjectURL(blob);

          loader.load(
            url,
            (gltf) => {
              const vrm = gltf.userData.vrm as VRM;

              // Cache the VRM for future use in this session
              globalVRMCache.main = vrm;
              globalVRMCache.mainLoaded = true;
              setupVRM(vrm);

              // Clean up the blob URL
              URL.revokeObjectURL(url);
            },
            undefined,
            (error) => {
              console.error('❌ Error parsing VRM:', error);
              URL.revokeObjectURL(url);
            }
          );
        })
        .catch((error) => {
          console.error('❌ Error loading VRM with cache:', error);
        });
    }

    function setupVRM(vrm: VRM) {
      if (vrm) {
        // Clear scene again before adding new VRM
        scene.traverse((child) => {
          if (child.userData.vrm) {
            scene.remove(child);
          }
        });

        // Position and scale the avatar (moved down for face focus)
        vrm.scene.position.set(0, -0.7, 0);
        vrm.scene.rotation.set(0, 0, 0);
        vrm.scene.scale.set(0.8, 0.68, 0.8);

        // Mark the scene as VRM for identification
        vrm.scene.userData.vrm = true;

        // Enhance VRM materials for better visual quality (preserve toon shading)
        vrm.scene.traverse((child: any) => {
          if (child.isMesh && child.material) {
            // Enable shadows
            child.castShadow = true;
            child.receiveShadow = true;

            // Handle MToon materials (2D toon shader) specially
            if (child.material.type === 'MToonMaterial' || child.material.isMToonMaterial) {
              // Preserve toon shader properties - don't modify them
              // Just ensure updates are applied
              child.material.needsUpdate = true;
            }
            // Handle standard materials
            else if (child.material.isMeshStandardMaterial) {
              // Only enhance non-toon materials
              child.material.metalness = Math.min(child.material.metalness * 1.1, 1);
              child.material.roughness = Math.max(child.material.roughness * 0.9, 0.1);
              child.material.flatShading = false;
              child.material.needsUpdate = true;
            }
            // Handle other material types
            else {
              child.material.needsUpdate = true;
            }
          }
        });

        scene.add(vrm.scene);
        vrmRef.current = vrm;

        // Create animation mixer for the VRM
        const mixer = new THREE.AnimationMixer(vrm.scene);
        mixerRef.current = mixer;

        // Load greeting animation (cache it but don't play yet)
        loadGreetingAnimation(vrm, mixer);

        // Load speaking animation (cache it but don't play yet)
        loadSpeakingAnimation(vrm, mixer);

        // Load idle animation (cache it)
        loadIdleAnimation(vrm, mixer);

        setIsLoaded(true);
        onLoadComplete?.();
      }
    }

    function loadGreetingAnimation(vrm: VRM, mixer: THREE.AnimationMixer) {
      if (!globalVRMCache.greeting || !globalVRMCache.greetingLoaded) {
        const greetingLoader = new GLTFLoader();
        greetingLoader.register((parser) => new VRMAnimationLoaderPlugin(parser));

        // Load greeting animation with IndexedDB caching
        loadFileWithCache(toProxyUrl(VRM_ANIMATION_PATHS.GREETING))
          .then((arrayBuffer) => {
            // Convert ArrayBuffer to Blob for GLTFLoader
            const blob = new Blob([arrayBuffer], { type: 'application/octet-stream' });
            const url = URL.createObjectURL(blob);

            greetingLoader.load(
              url,
              (greetingGltf) => {
                let vrmGreetingAnimation = greetingGltf.userData.vrmAnimation as VRMAnimation;

                if (!vrmGreetingAnimation && greetingGltf.userData.vrmAnimations) {
                  const vrmAnimations = greetingGltf.userData.vrmAnimations as VRMAnimation[];
                  vrmGreetingAnimation = vrmAnimations[0];
                }

                if (vrmGreetingAnimation) {
                  globalVRMCache.greeting = vrmGreetingAnimation;
                  globalVRMCache.greetingLoaded = true;
                  setupGreetingAction(vrm, mixer, vrmGreetingAnimation);
                }

                // Clean up object URL
                URL.revokeObjectURL(url);
              },
              undefined,
              (error) => {
                console.warn('Could not load greeting animation:', error);
                URL.revokeObjectURL(url);
              }
            );
          })
          .catch((error) => {
            console.warn('Could not cache greeting animation:', error);
          });
      } else {
        setupGreetingAction(vrm, mixer, globalVRMCache.greeting);
      }
    }

    function loadSpeakingAnimation(vrm: VRM, mixer: THREE.AnimationMixer) {
      if (!globalVRMCache.speaking || !globalVRMCache.speakingLoaded) {
        const animationLoader = new GLTFLoader();
        animationLoader.register((parser) => new VRMAnimationLoaderPlugin(parser));

        // Load speaking animation with IndexedDB caching
        loadFileWithCache(toProxyUrl(VRM_ANIMATION_PATHS.SPEAKING))
          .then((arrayBuffer) => {
            // Convert ArrayBuffer to Blob for GLTFLoader
            const blob = new Blob([arrayBuffer], { type: 'application/octet-stream' });
            const url = URL.createObjectURL(blob);

            animationLoader.load(
              url,
              (animationGltf) => {
                let vrmAnimation = animationGltf.userData.vrmAnimation as VRMAnimation;

                if (!vrmAnimation && animationGltf.userData.vrmAnimations) {
                  const vrmAnimations = animationGltf.userData.vrmAnimations as VRMAnimation[];
                  vrmAnimation = vrmAnimations[0];
                }

                if (vrmAnimation) {
                  globalVRMCache.speaking = vrmAnimation;
                  globalVRMCache.speakingLoaded = true;
                  setupSpeakingAction(vrm, mixer, vrmAnimation);
                }

                // Clean up object URL
                URL.revokeObjectURL(url);
              },
              undefined,
              (error) => {
                console.warn('Could not load speaking animation:', error);
                URL.revokeObjectURL(url);
              }
            );
          })
          .catch((error) => {
            console.warn('Could not cache speaking animation:', error);
          });
      } else {
        setupSpeakingAction(vrm, mixer, globalVRMCache.speaking);
      }
    }

    function loadIdleAnimation(vrm: VRM, mixer: THREE.AnimationMixer) {
      if (!globalVRMCache.idle || !globalVRMCache.idleLoaded) {
        const animationLoader = new GLTFLoader();
        animationLoader.register((parser) => new VRMAnimationLoaderPlugin(parser));

        const idleUrl = toProxyUrl(VRM_ANIMATION_PATHS.IDLE);

        // Load idle animation with IndexedDB caching
        loadFileWithCache(idleUrl)
          .then((arrayBuffer) => {
            // Convert ArrayBuffer to Blob for GLTFLoader
            const blob = new Blob([arrayBuffer]);
            const url = URL.createObjectURL(blob);

            animationLoader.load(
              url,
              (animationGltf) => {
                let vrmAnimation = animationGltf.userData.vrmAnimation as VRMAnimation;

                if (!vrmAnimation && animationGltf.userData.vrmAnimations) {
                  const vrmAnimations = animationGltf.userData.vrmAnimations as VRMAnimation[];
                  vrmAnimation = vrmAnimations[0];
                }

                if (vrmAnimation) {
                  globalVRMCache.idle = vrmAnimation;
                  globalVRMCache.idleLoaded = true;
                  setupIdleAction(vrm, mixer, vrmAnimation);
                }

                // Clean up the blob URL
                URL.revokeObjectURL(url);
              },
              undefined,
              (error) => {
                console.warn('Could not load idle animation:', error);
                URL.revokeObjectURL(url);
              }
            );
          })
          .catch((error) => {
            console.warn('Could not load idle animation with cache:', error);
          });
      } else {
        console.log('🚀 Using cached idle animation');
        setupIdleAction(vrm, mixer, globalVRMCache.idle);
      }
    }

    function setupSpeakingAction(vrm: VRM, mixer: THREE.AnimationMixer, vrmAnimation: VRMAnimation) {
      const speakingClip = createVRMAnimationClip(vrmAnimation, vrm);
      const speakingAction = mixer.clipAction(speakingClip);
      speakingAction.loop = THREE.LoopRepeat;
      speakingAction.setEffectiveWeight(1.0);
      speakingActionRef.current = speakingAction;

      // Don't auto-play - will be controlled by speech state
      console.log('🎙️ Speaking animation setup complete');
    }

    function setupGreetingAction(vrm: VRM, mixer: THREE.AnimationMixer, vrmAnimation: VRMAnimation) {
      const greetingClip = createVRMAnimationClip(vrmAnimation, vrm);
      const greetingAction = mixer.clipAction(greetingClip);
      greetingAction.loop = THREE.LoopOnce;
      greetingAction.clampWhenFinished = true;
      greetingAction.setEffectiveWeight(1.0);

      const onGreetingFinished = () => {
        isGreetingPlayingRef.current = false;
        greetingAction.stop();
        greetingAction.reset(); // Reset for next play
        if (idleActionRef.current) {
          idleActionRef.current.reset();
          idleActionRef.current.play();
        }
        console.log('🎭 Greeting animation finished, returning to idle');
      };

      mixer.addEventListener('finished', onGreetingFinished);
      greetingActionRef.current = greetingAction;

      // Don't auto-play on load - wait for greetingTrigger
    }

    function setupIdleAction(vrm: VRM, mixer: THREE.AnimationMixer, vrmAnimation: VRMAnimation) {
      const animationClip = createVRMAnimationClip(vrmAnimation, vrm);
      const animationAction = mixer.clipAction(animationClip);
      animationAction.loop = THREE.LoopRepeat;
      animationAction.setEffectiveWeight(1.0);
      idleActionRef.current = animationAction;

      // Start idle animation immediately since greeting waits for trigger
      animationAction.play();
    }

    return () => {
      if (vrmRef.current) {
        scene.remove(vrmRef.current.scene);
        // Clean up VRM resources manually
        vrmRef.current.scene.traverse((child) => {
          if (child instanceof THREE.Mesh) {
            if (child.geometry) child.geometry.dispose();
            if (child.material) {
              if (Array.isArray(child.material)) {
                child.material.forEach(material => material.dispose());
              } else {
                child.material.dispose();
              }
            }
          }
        });
        vrmRef.current = null;
      }

      // Clean up animation mixer
      if (mixerRef.current) {
        mixerRef.current.stopAllAction();
        mixerRef.current = null;
      }
      if (idleActionRef.current) {
        idleActionRef.current = null;
      }
      if (greetingActionRef.current) {
        greetingActionRef.current = null;
      }
      if (speakingActionRef.current) {
        speakingActionRef.current = null;
      }
      isGreetingPlayingRef.current = false;
    };
  }, [scene, playGreeting, onLoadComplete]);

  // Effect to handle greeting animation trigger
  useEffect(() => {
    if (playGreeting && greetingActionRef.current && greetingTrigger && greetingTrigger > 0) {
      // Stop idle animation to give greeting priority
      if (idleActionRef.current) {
        idleActionRef.current.stop();
        console.log('🛑 Stopped idle animation for greeting');
      }

      // Stop and reset greeting animation if it's already playing
      if (isGreetingPlayingRef.current) {
        greetingActionRef.current.stop();
      }

      // Start greeting animation
      isGreetingPlayingRef.current = true;
      greetingActionRef.current.reset();
      greetingActionRef.current.play();

      console.log('🎭 Playing greeting animation, trigger:', greetingTrigger);
    }
  }, [playGreeting, greetingTrigger]);

  useEffect(() => {
    if (audioUrl && isLoaded) {
      console.log('🎧 VRMAvatar received audioUrl:', audioUrl);
      const audio = new Audio(audioUrl);
      audio.crossOrigin = 'anonymous';
      audio.preload = 'auto';
      audioRef.current = audio;

      // Add more detailed audio event logging
      audio.addEventListener('loadstart', () => console.log('📥 Audio loadstart'));
      audio.addEventListener('loadedmetadata', () => {
        console.log('📊 Audio metadata loaded:', {
          duration: audio.duration,
          readyState: audio.readyState,
          networkState: audio.networkState
        });
      });
      audio.addEventListener('canplay', () => console.log('▶️ Audio can play'));
      audio.addEventListener('canplaythrough', () => console.log('⏭️ Audio can play through'));

      setupAudioAnalysis(audio);

      // Add event listeners to track audio state
      const handleAudioEnd = () => {
        console.log('🏁 Audio playback ended');
        // Immediately reset facial expressions when audio ends
        resetFacialExpressions();
        onAudioEnd?.();
      };

      const handleAudioError = (e: Event) => {
        console.error('❌ Audio error:', e);
        if (audio.error) {
          console.error('❌ Audio error details:', {
            code: audio.error.code,
            message: audio.error.message
          });
        }
      };

      audio.addEventListener('ended', handleAudioEnd);
      audio.addEventListener('error', handleAudioError);

      return () => {
        if (audio) {
          audio.removeEventListener('ended', handleAudioEnd);
          audio.removeEventListener('error', handleAudioError);
          audio.pause();
          audio.src = '';
          audio.load();
        }
      };
    }
  }, [audioUrl, isLoaded, setupAudioAnalysis, onAudioEnd, resetFacialExpressions]);

  useEffect(() => {
    const audio = audioRef.current;
    if (audio && isLoaded) {
      if (isPlaying) {
        console.log('🎬 Attempting to play audio');
        // Only reset to beginning if not resuming from pause
        if (!isPaused) {
          audio.currentTime = 0; // Start from beginning
        }
        const playPromise = audio.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              console.log('✅ Audio playback started successfully');
            })
            .catch(error => {
              console.error('❌ Audio play failed:', error);
            });
        }


      } else {
        audio.pause();
      }
    }
  }, [isPlaying, isLoaded, isPaused]);

  useFrame((state, delta) => {
    if (vrmRef.current) {
      // Update VRM first
      vrmRef.current.update(delta);

      // Update animation mixer for idle, greeting, and speaking animations
      if (mixerRef.current) {
        mixerRef.current.update(delta);

        // Handle animation priorities: greeting > speaking > idle
        if (isGreetingPlayingRef.current && greetingActionRef.current) {
          // During greeting: stop both idle and speaking animations
          if (idleActionRef.current && idleActionRef.current.isRunning()) {
            idleActionRef.current.stop();
            console.log('🛑 Force-stopped idle during greeting');
          }
          if (speakingActionRef.current && speakingActionRef.current.isRunning()) {
            speakingActionRef.current.stop();
            console.log('🛑 Force-stopped speaking during greeting');
          }
        }
        else if (isPlaying && !isGreetingPlayingRef.current && speakingActionRef.current) {
          // During speech (but not greeting): play speaking animation, stop idle
          if (idleActionRef.current && idleActionRef.current.isRunning()) {
            idleActionRef.current.stop();
            console.log('🛑 Stopped idle for speaking');
          }
          if (!speakingActionRef.current.isRunning()) {
            speakingActionRef.current.reset();
            speakingActionRef.current.play();
            console.log('🎙️ Started speaking animation');
          } else {
            console.log('🎙️ Speaking animation already running');
          }
        }
        else if (!isPlaying && !isGreetingPlayingRef.current) {
          // Not speaking and not greeting: stop speaking, resume idle
          if (speakingActionRef.current && speakingActionRef.current.isRunning()) {
            speakingActionRef.current.stop();
            console.log('🛑 Stopped speaking animation');
          }
          if (idleActionRef.current && !idleActionRef.current.isRunning()) {
            idleActionRef.current.reset();
            idleActionRef.current.play();
            console.log('▶️ Resumed idle animation');
          }
        }
      }

      // Check if we should run viseme analysis - either HTML5 audio OR external Web Audio API
      const shouldRunVisemeAnalysis = isPlaying && (
        (audioRef.current && !audioRef.current.paused) || // HTML5 audio approach
        (externalAnalyser && externalAnalyserData)         // Web Audio API approach
      );

      if (shouldRunVisemeAnalysis) {
        // Get viseme data for proper lipsync
        const visemeData = calculateVisemeData();

        // Only apply manual bone movement during speech if greeting is not playing
        if (!isGreetingPlayingRef.current) {
          const time = state.clock.elapsedTime;
          const headBone = vrmRef.current.humanoid?.getNormalizedBoneNode(VRMHumanBoneName.Head);
          const neckBone = vrmRef.current.humanoid?.getNormalizedBoneNode(VRMHumanBoneName.Neck);

          // Natural head and neck movement (only during speech, not greeting)
          if (headBone) {
            headBone.rotation.y = Math.sin(time * 0.5) * 0.1;
            headBone.rotation.x = Math.sin(time * 0.3) * 0.05;
          }

          if (neckBone) {
            neckBone.rotation.y = Math.sin(time * 0.4) * 0.08;
          }

          // Pass head rotation data to updateBlendShapes for camera-looking eyes
          const headRotation = headBone ? { x: headBone.rotation.x, y: headBone.rotation.y } : { x: 0, y: 0 };
          const neckRotation = neckBone ? { x: neckBone.rotation.x, y: neckBone.rotation.y } : { x: 0, y: 0 };

          updateBlendShapes(visemeData, headRotation, neckRotation);
        } else {
          // During greeting animation, only do facial expressions without manual bone movement
          updateBlendShapes(visemeData);
          console.log('🎭 Skipping manual bone movement during greeting animation');
        }
      }
    }
  });

  return null;
};

const VRMAvatar: React.FC<VRMAvatarProps> = (props) => {
  return (
    <div style={{ width: '100%', height: '100%' }}>
      <Canvas
        key="vrm-canvas"
        camera={{ position: [0, 0.1, 1.5], fov: 50 }}
        gl={{
          antialias: true,
          toneMapping: THREE.LinearToneMapping,
          toneMappingExposure: 1.0,
          outputColorSpace: THREE.SRGBColorSpace
        }}
        onCreated={({ scene, gl }) => {
          // Clear any existing VRM models on canvas creation
          scene.traverse((child) => {
            if (child.userData.vrm) {
              scene.remove(child);
            }
          });

          // Enhanced rendering settings for VRM
          gl.shadowMap.enabled = true;
          gl.shadowMap.type = THREE.PCFSoftShadowMap;
        }}
      >
        {/* Balanced lighting for 2D toon shaders */}
        <ambientLight intensity={1.0} color={0xffffff} />
        <directionalLight
          position={[1, 3, 3]}
          intensity={0.9}
          color={0xffffff}
          castShadow
          shadow-mapSize-width={1024}
          shadow-mapSize-height={1024}
          shadow-camera-near={0.1}
          shadow-camera-far={8}
          shadow-camera-left={-2}
          shadow-camera-right={2}
          shadow-camera-top={2}
          shadow-camera-bottom={-2}
        />
        {/* Fill light from opposite side */}
        <directionalLight
          position={[-0.5, 1.5, 2]}
          intensity={0.4}
          color={0xfff8dc}
        />
        {/* Back rim light for depth */}
        <pointLight
          position={[0, 1, -1]}
          intensity={0.3}
          color={0xe6f3ff}
        />
        <VRMAvatarModel key="vrm-model" {...props} />
      </Canvas>
    </div>
  );
};

export default VRMAvatar;